import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { createCheckoutSession, manageSubscription } from "@/features/billing/api/billing";
import { hasProTrialPromo } from "@/features/billing/promo";
import { usePlanCatalog } from "@/features/pricing/hooks/usePlanCatalog";
import { useUsage } from "@/features/usage/hooks/useUsage";
import { isProPlan } from "@/utils/plan";
import { AlertTriangle, Check, Crown, Settings2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

interface ProPlanProps {
  isLoading: boolean;
  title: string;
  price: string;
  features: string[];
}

const useProPlanProps = (): ProPlanProps => {
  const { data: planCatalog, isLoading } = usePlanCatalog();
  const [searchParams] = useSearchParams();
  const proPlan = planCatalog?.plans.find((plan) => plan.id === "pro");
  const includeTrial = hasProTrialPromo(searchParams.get("promo"));

  return useMemo(() => {
    const trialSuffix = includeTrial ? " (free for the first 30 days)" : "";
    return {
      isLoading: isLoading || !proPlan,
      title: 'Paperclip Pro',
      // Note that this reflects the price stored in our database
      // which may be different in production than in other environments.
      price: !proPlan ? '' : `$${(proPlan.price?.amount ?? 0) / 100}/${proPlan.price?.cadence ?? 'month'}${trialSuffix}`,
      features: !proPlan ? [] : [
        `${proPlan.limits.dailyMaps.total?.toLocaleString() ?? 'unlimited'} documents/day quota`,
        `${proPlan.capabilities.heavyGrepStartsPerMinute.total?.toLocaleString() ?? 'unlimited'} corpus-wide greps (started) per minute`,
        `${proPlan.capabilities.heavySearchStartsPerMinute.total?.toLocaleString() ?? 'unlimited'} searches (started) per minute`,
        proPlan.capabilities.heavyQueuePriority.tier < 2 ? 'Greps and searches jump to a priority queue' : 'Greps and searches are given standard priority',
      ],
    }
  }, [planCatalog, isLoading, includeTrial, proPlan]);
}

const formatPeriodEnd = (isoDate: string | null): string => {
  if (!isoDate) return "the end of your billing period";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "the end of your billing period";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const PlanCard = () => {
  const { data: usageData, isLoading } = useUsage();
  const proPlanProps = useProPlanProps();

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);

  const isPro = useMemo(() => isProPlan(usageData?.planId), [usageData]);

  const showCancelBanner =
    isPro &&
    usageData?.subscription?.cancelAtPeriodEnd === true &&
    (usageData?.subscription?.status === "active" || usageData?.subscription?.status === "trialing");

  const handleUpgrade = useCallback(async () => {
    setCheckoutLoading(true);
    try {
      const { checkout_url } = await createCheckoutSession();
      window.location.href = checkout_url;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to start checkout.";
      toast.error(message);
    } finally {
      setCheckoutLoading(false);
    }
  }, []);

  const handleManage = useCallback(async () => {
    setManageLoading(true);
    try {
      const { portal_url } = await manageSubscription();
      window.location.href = portal_url;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to open subscription manager.";
      toast.error(message);
    } finally {
      setManageLoading(false);
    }
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Crown className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <CardTitle>{isPro ? "Plan" : "Increase your limits"}</CardTitle>
            <CardDescription>
              {isPro
                ? "You're on the Pro plan. Thank you!"
                : "Upgrade to Pro for higher limits and priority features."}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {showCancelBanner && (
          <div
            role="alert"
            className="mb-4 flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600"
                aria-hidden="true"
              />
              <p className="text-sm text-amber-900">
                Your Pro plan is canceled. You keep access until{" "}
                <strong>
                  {formatPeriodEnd(
                    usageData?.subscription?.currentPeriodEnd ?? null,
                  )}
                </strong>
                . You can resume it from the subscription manager.
              </p>
            </div>
            <Button
              variant="outline"
              className="flex-shrink-0 border-amber-300"
              loading={manageLoading}
              onClick={handleManage}
            >
              <Settings2 className="h-4 w-4" aria-hidden="true" />
              Resume plan
            </Button>
          </div>
        )}
        {(isLoading || proPlanProps.isLoading) ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-zinc-900 border-t-transparent" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-zinc-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-500">Current plan</span>
                <Badge variant={isPro ? "blue" : "default"}>
                  {usageData?.planName ?? "—"}
                </Badge>
              </div>
            </div>

            {isPro ? (
              <Button
                variant="outline"
                className="w-full"
                loading={manageLoading}
                onClick={handleManage}
              >
                <Settings2 className="w-4 h-4" />
                Manage Subscription
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-zinc-700 font-bold">What you'll get with Pro:</p>
                <ul className="space-y-2">
                  {proPlanProps.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-sm text-zinc-700"
                    >
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-zinc-500">
                    {proPlanProps.price}
                  </span>
                  <Button
                    loading={checkoutLoading}
                    onClick={handleUpgrade}
                  >
                    <Crown className="w-4 h-4" />
                    Upgrade to Pro
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
